import { useState, useEffect, useLayoutEffect, useRef, Suspense } from "react";
// Route/layout chunks are lazy-loaded through lazyWithReload (a React.lazy that
// recovers from stale-chunk failures after a deploy) rather than React.lazy
// directly. Aliased to `lazy` so every call site below reads the same. See
// src/lib/chunk-reload.
import { lazyWithReload as lazy, reloadIfStaleBuildPending } from "@/lib/chunk-reload";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { UpdateAvailableBanner } from "@/components/update/UpdateAvailableBanner";
import { BrandedLoader } from "@/components/ui/BrandedLoader";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { SplashScreen } from "@capacitor/splash-screen";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { QueryCachePersistence } from "./components/QueryCachePersistence";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { CompanyProvider } from "./contexts/CompanyContext";
import { ProviderWrapper } from "./contexts/ProviderWrapper";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthGateNotice } from "./components/auth/AuthGateNotice";
import { markAppInteractive } from "@/lib/perf-monitor";
import { prefetchRoutesWhenIdle, type ChunkLoader } from "@/lib/route-prefetch";
import { PROFILE_UNAVAILABLE_MESSAGE } from "@/lib/profile-fetch-outcome";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ThemeProvider } from "./components/theme/ThemeProvider";
import { MobileRouteGuard } from "./components/MobileRouteGuard";
import { isSchedulingOnly, type Role } from "./lib/mobile-mode";
import { KioskEntryGuard } from "./components/pin-access/KioskEntryGuard";
import { FeatureGate } from "./components/FeatureGate";
import { FeatureRedirect } from "./components/FeatureRedirect";
import { useViewportHeightSync } from "./hooks/useViewportHeightSync";
import { listenForNotificationTaps } from "./lib/push-notifications";
import { createDeferredNavigator, type DeferredNavigator } from "./lib/push-nav-buffer";
import { listenForDeepLinks } from "./lib/deep-links";
import { ProtectedPinRoute } from "./components/pin-access/ProtectedPinRoute";
import { isPinSession as checkPinSession, getPinSessionData } from "@/lib/pin-session-utils";
import { LoginUnreadBanner } from "./components/messaging/LoginUnreadBanner";
import { GlobalMessageNotifications } from "./components/messaging/GlobalMessageNotifications";
import { AppIconBadgeSync } from "./components/messaging/mobile/AppIconBadgeSync";
import { Capacitor } from "@capacitor/core";

// Every route page below is lazy-loaded. Before this, all ~35 pages were
// bundled eagerly into one ~5.3MB (1.3MB gzipped) JS chunk that had to
// download and parse in full before the FIRST screen (even /auth) could
// render — the actual cause of the multi-second white screen on launch
// reported in #817/#822: the perf work already landed there sped up the
// auth/profile waterfall, but that waterfall couldn't start until this one
// giant chunk finished loading. Splitting per-route means a cold launch only
// pays for the page it's about to show.
// MainLayout (sidebar + header shell wrapping every authenticated route) pulls
// in NotificationCenter, InteractiveTour, HelpCenterHost, FeedbackButton, and
// half a dozen other subtrees — despite being a single non-page component, it
// was one of the largest remaining contributors to the entry chunk after the
// route-level split below. Lazy-loading it means a cold /auth or /landing
// load (neither of which renders it) no longer pays for it (#817, reopened).
const MainLayout = lazy(() =>
  import("./components/layout/MainLayout").then((m) => ({ default: m.MainLayout }))
);
const Auth = lazy(() => import("./pages/Auth").then((m) => ({ default: m.Auth })));
// DemoSignup and Signup pages remain on disk so an invitation-token flow
// can reinstate them later, but they are not wired to any route — public
// sign-up is disabled and the /signup + /demo-signup routes redirect to
// /auth. Re-add these imports if/when invite-only signup ships.
const Demo = lazy(() => import("./pages/Demo"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TeamCommsGated = lazy(() => import("./pages/TeamCommsGated"));
const TeamComms = lazy(() => import("./pages/TeamComms"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Reports = lazy(() => import("./pages/Reports"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const Schedules = lazy(() => import("./pages/Schedules"));
const SchedulesTimesheets = lazy(() => import("./pages/SchedulesTimesheets"));
const Timeclock = lazy(() => import("./pages/Timeclock"));
const SchedulesTimeOff = lazy(() => import("./pages/SchedulesTimeOff"));
const SchedulesWorkTrips = lazy(() => import("./pages/SchedulesWorkTrips"));
const SchedulesRequests = lazy(() => import("./pages/SchedulesRequests"));
const SchedulesAvailability = lazy(() => import("./pages/SchedulesAvailability"));
const SchedulesCompliance = lazy(() => import("./pages/SchedulesCompliance"));
const SchedulesBreakLog = lazy(() => import("./pages/SchedulesBreakLog"));
const SchedulesTips = lazy(() => import("./pages/SchedulesTips"));
const SchedulesInsights = lazy(() => import("./pages/SchedulesInsights"));
const Settings = lazy(() => import("./pages/Settings"));
const Account = lazy(() => import("./pages/Account"));
const Help = lazy(() => import("./pages/Help"));
const Tasks = lazy(() => import("./pages/Tasks"));
const TaskTemplates = lazy(() => import("./pages/TaskTemplates"));
const Training = lazy(() => import("./pages/Training"));
const Integrations = lazy(() => import("./pages/Integrations"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings").then((m) => ({ default: m.NotificationSettings })));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmployeeLimitedView = lazy(() => import("./pages/EmployeeLimitedView"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SharedLogin = lazy(() => import("./pages/SharedLogin").then((m) => ({ default: m.SharedLogin })));
const KioskView = lazy(() => import("./pages/KioskView"));
const OnTheClock = lazy(() => import("./pages/OnTheClock"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const DailyChecklists = lazy(() => import("./pages/DailyChecklists"));
const DailyChecklistTemplates = lazy(() => import("./pages/DailyChecklistTemplates"));
const PrepHistory = lazy(() => import("./pages/PrepHistory"));
const ChecklistCompletionReport = lazy(() => import("./pages/ChecklistCompletionReport"));
const ParSheets = lazy(() => import("./pages/ParSheets"));
const CogsIntelligence = lazy(() => import("./pages/CogsIntelligence"));
const BrandStandards = lazy(() => import("./pages/BrandStandards"));
const TeamSurveys = lazy(() => import("./pages/TeamSurveys"));
const MySurveys = lazy(() => import("./pages/MySurveys"));
const Tickets = lazy(() => import("./pages/Tickets"));
const MyAgreements = lazy(() => import("./pages/MyAgreements"));
const MyWaiver = lazy(() => import("./pages/MyWaiver"));
const EmployeeFiles = lazy(() => import("./pages/EmployeeFiles"));
const AssociatePhoneList = lazy(() => import("./pages/AssociatePhoneList"));
const CounselingSessions = lazy(() => import("./pages/CounselingSessions"));
const NewHireOnboarding = lazy(() => import("./pages/NewHireOnboarding"));
const DailyLogs = lazy(() => import("./pages/DailyLogs"));
const Messages = lazy(() => import("./pages/Messages"));
const SchedulesMessages = lazy(() => import("./pages/SchedulesMessages"));
const Onboard = lazy(() => import("./pages/Onboard"));
const Interest = lazy(() => import("./pages/Interest"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyChoices = lazy(() => import("./pages/PrivacyChoices"));
const Landing = lazy(() => import("./pages/Landing"));
import { CookieBanner } from "./components/consent/CookieBanner";
import { shouldShowConsentBanner, readKioskDeviceSessionToken } from "@/lib/kiosk-surface";
import { AnalyticsLoader } from "./components/consent/AnalyticsLoader";
import { BiometricLockGuard } from "./components/BiometricLockGuard";
import { resolveRootRoute } from "./lib/root-route";

// Password reset utility removed for security - use admin interface instead

// Same specifiers as the lazy() routes above, so a prefetched chunk is the one
// the route renders. Ordered by how often each page is opened.
const MOST_USED_ROUTE_CHUNKS: ChunkLoader[] = [
  () => import("./components/layout/MainLayout"),
  () => import("./pages/Dashboard"),
  () => import("./pages/Schedules"),
  () => import("./pages/Messages"),
  () => import("./pages/Tasks"),
  () => import("./pages/DailyChecklists"),
  () => import("./pages/TeamCommsGated"),
];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

const AppContent = () => {
  const { user, profile, loading, profileLoading, loginMethod, authError, authErrorRetryable, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();

  // Keep --app-vh CSS custom property in sync with visualViewport so
  // fixed-height surfaces don't hide under the iOS keyboard.
  useViewportHeightSync();

  // Once a signed-in email user is past the gate, pull the code for the pages
  // they open most in idle time, so the first tap on each doesn't wait on a
  // chunk download (#1246). Kiosk sessions load their own lazy tabs.
  const warmRoutes = !!user && !!profile && loginMethod === 'email';
  useEffect(() => {
    if (!warmRoutes) return;
    return prefetchRoutesWhenIdle(MOST_USED_ROUTE_CHUNKS);
  }, [warmRoutes]);

  // Native only: route notification taps and Universal Links to the screen the
  // payload points at (Capacitor buffers the cold-start tap until a listener
  // attaches). MobileRouteGuard still applies, so a target the session can't
  // see lands on /dashboard instead.
  //
  // Both go through a deferred navigator because the cold-start tap arrives
  // BEFORE auth resolves: at that point the path is still '/', which counts as
  // a public route, so the logged-out tree renders and an authenticated target
  // matches only the wildcard fallback — the app settles on /dashboard and the
  // notification reads as doing nothing (reported 2026-09-05 on the first
  // announcement push). See push-nav-buffer.ts.
  const sessionReady = !!(user && profile);
  const deferredNav = useRef<DeferredNavigator | null>(null);
  if (!deferredNav.current) {
    deferredNav.current = createDeferredNavigator((path) => navigate(path));
  }
  useEffect(() => {
    deferredNav.current?.setReady(sessionReady);
  }, [sessionReady]);

  useEffect(
    () => listenForNotificationTaps((path) => deferredNav.current?.request(path)),
    [],
  );
  useEffect(() => listenForDeepLinks((path) => deferredNav.current?.request(path)), []);

  // Native: hide the launch splash the moment auth first resolves, so it tracks
  // actual readiness instead of a fixed timer — no splash→spinner double-load,
  // and no needless hold once warm-start cache makes the app interactive fast.
  // capacitor.config sets launchAutoHide:true (launchShowDuration 1500), so iOS
  // self-dismisses the splash at ~1.5s; this hide() is a belt-and-suspenders
  // early-hide that beats that timer when auth resolves fast, not the sole
  // dismissal mechanism. A 3s failsafe in main.tsx backstops a hang. hide() is
  // idempotent, so calling it alongside the OS auto-hide is safe.
  useEffect(() => {
    if (!loading && Capacitor.isNativePlatform()) {
      SplashScreen.hide().catch(() => {});
    }
  }, [loading]);

  // Reactive check for VALID PIN session to prevent flash during login
  const [hasValidPinSession, setHasValidPinSession] = useState(() => {
    try {
      const isPinSessionActive = checkPinSession();
      const pinData = getPinSessionData();
      // Only return true if we have a valid PIN session AND no email auth user
      return isPinSessionActive && pinData && !user;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const checkValidPinSession = () => {
      try {
        const isPinSessionActive = checkPinSession();
        const pinData = getPinSessionData();
        // Only set to true if we have a valid PIN session AND no email auth user
        setHasValidPinSession(isPinSessionActive && pinData && !user);
      } catch {
        setHasValidPinSession(false);
      }
    };

    // Listen for PIN session updates
    window.addEventListener('pin_session_updated', checkValidPinSession);
    window.addEventListener('storage', checkValidPinSession);

    // Re-check when user state changes
    checkValidPinSession();

    return () => {
      window.removeEventListener('pin_session_updated', checkValidPinSession);
      window.removeEventListener('storage', checkValidPinSession);
    };
  }, [user]);

  // App state logging moved to debug utility

  const location = useLocation();

  // Feed every location change back to the deferred navigator. A cold-started
  // user runs a landing chain ('/' -> /dashboard, then Dashboard sends
  // scheduling-only roles to /schedules) that renders AFTER the push target is
  // applied and replaces it — on device a tap aimed at /schedules/messages
  // ended up on /schedules. This lets the target re-assert until it sticks,
  // bounded so a genuinely blocked path can't loop.
  useEffect(() => {
    deferredNav.current?.confirm(location.pathname);
  }, [location.pathname]);

  // A deploy that landed while the user sat on the previous screen is picked up
  // HERE, on the way to the next one — never mid-form. See the "DEFERRED to the
  // next route change" note in src/lib/chunk-reload.ts. Layout effect so the
  // reload starts before the new route paints on stale assets. The first run
  // is the initial mount, not a navigation, and there is nothing pending yet.
  const isFirstPathname = useRef(true);
  useLayoutEffect(() => {
    if (isFirstPathname.current) {
      isFirstPathname.current = false;
      return;
    }
    reloadIfStaleBuildPending();
  }, [location.pathname]);

  // Define public routes that don't require authentication
  // '/' is intentionally listed here so the root landing page bypasses
  // the auth-loading screen for first-time visitors. We then conditionally
  // route '/' below: logged-out visitors see <Landing/>, authenticated
  // users redirect to their appropriate destination.
  // Public route prefixes -- '/' is checked separately because string
  // .startsWith('/') matches EVERY path, which previously made
  // isPublicRoute always true and short-circuited the auth-loading
  // gate. That caused fresh deep-URL navigation (paste /schedules/
  // timesheets into the address bar) to bounce to /dashboard because
  // the conditional auth-only Route block hadn't rendered yet when
  // React Router matched the wildcard fallback.
  const PUBLIC_PREFIXES = ['/limited', '/kiosk', '/investor-pitch', '/auth', '/signup', '/demo', '/demo-signup', '/reset-password', '/shared-login', '/onboard', '/interest', '/new-hire', '/privacy-policy', '/terms-of-service', '/do-not-sell'];
  const isPublicRoute =
    location.pathname === '/' ||
    PUBLIC_PREFIXES.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    ) ||
    location.pathname.includes('/kiosk') || // Match /c/:companyCode/kiosk
    location.pathname.includes('/shared-login'); // Match /c/:companyCode/shared-login

  // Show loading state for protected routes during initial auth, OR whenever
  // user is authenticated but profile is still loading. The latter prevents
  // a homepage/auth-page flash during the post-login window between Supabase
  // setting the user and AuthContext finishing the deferred profile fetch —
  // without it, /dashboard routes don't yet exist, the wildcard bounces to
  // /auth, Auth.tsx's session check then navigates to /, and Landing renders
  // for a frame before profile load completes.
  if ((loading && !isPublicRoute) || (user && !profile && profileLoading)) {
    return <BrandedLoader />;
  }

  // Determine user context
  const isPinSession = loginMethod === 'pin' && profile && !user;
  const isSuperAdmin = profile?.role === 'super_admin';
  const isEmployee = profile?.role === 'employee';
  const hasUserAndProfile = user && profile;
  const hasUserButNoProfile = user && !profile && !loading && !profileLoading;

  const userName = profile ? `${profile.first_name} ${profile.last_name}` : 'Guest';
  const pinSessionData = checkPinSession() ? getPinSessionData() : null;
  const userRole = (pinSessionData?.profile?.role || profile?.role || 'employee') as 'super_admin' | 'admin' | 'corporate' | 'lead_manager' | 'manager' | 'employee';

  // Scheduling-only sessions (employee/manager on a personal login) have no
  // messaging inbox — /messages is kiosk-only for them. Suppress the in-app
  // message banner + toasts so they aren't pinged about a surface they can't
  // open; announcements still arrive via push. Kiosk PIN sessions are
  // excluded by isSchedulingOnly (loginMethod === 'pin').
  const schedulingOnly = isSchedulingOnly(profile?.role as Role, loginMethod);

  // User context logging moved to debug utility

  // Show auth error if user exists but no profile
  // SKIP for public/kiosk routes - stale email auth should not block kiosk PIN login
  if ((hasUserButNoProfile || authError) && !isPublicRoute) {
    // `hasUserButNoProfile` with NO authError means the 8s profile-gate
    // failsafe cleared profileLoading while the fetch was still in flight —
    // transient by construction, so it gets the retry treatment too rather
    // than the "contact support" copy it used to inherit by default.
    const retryable = authErrorRetryable || (!authError && !!hasUserButNoProfile);
    return (
      <AuthGateNotice
        message={authError || PROFILE_UNAVAILABLE_MESSAGE}
        retryable={retryable}
        onRetry={refreshProfile}
        onSignOut={async () => {
          await signOut();
          window.location.href = '/auth';
        }}
      />
    );
  }

  // Past every gate above: this is the first render of real content, and the
  // number a user would call "how long it took to load". `load` alone misses
  // the auth + profile wait that sits on top of it. Idempotent.
  markAppInteractive();

  return (
    <>
      <AnalyticsLoader />
      {/* Never on a kiosk surface — the banner is bottom-pinned and lands on
          top of the PIN pad's bottom row and its submit button (see
          shouldShowConsentBanner). */}
      {!hasUserAndProfile && !isPinSession && shouldShowConsentBanner(location.pathname) && <CookieBanner />}
      {hasUserAndProfile && !isPinSession && !isSuperAdmin && !schedulingOnly && <LoginUnreadBanner />}
      {hasUserAndProfile && !isPinSession && !isSuperAdmin && !schedulingOnly && <GlobalMessageNotifications />}
      {/* App-icon badge upkeep. Unlike the two above this DOES run for
          scheduling-only roles — the phone is their surface, and theirs is the
          icon the red bubble was missing from. Kiosk/PIN sessions have no push
          tokens, so they're excluded. */}
      {hasUserAndProfile && !isPinSession && <AppIconBadgeSync />}
      <BiometricLockGuard>
      <Suspense fallback={<BrandedLoader />}>
      <Routes>
      {/* Investor pitch gated 2026-07: the page file stays on disk
          (src/pages/InvestorPitch.tsx) but is no longer publicly routed —
          the path bounces to login. Kept in PUBLIC_PREFIXES so logged-out
          visitors redirect immediately instead of hitting the auth-loading
          screen first. */}
      <Route path="/investor-pitch" element={<Navigate to="/auth" replace />} />

      {/* Marketing landing page lives on dashalign.com (dashalign-face-lift
          repo). The legacy /homepage-here route in this repo was taken
          down on 2026-05-30; a replacement landing will be authored in
          Brian's voice as a follow-up. */}

      {/* Legal / privacy surfaces — must remain reachable to logged-out
          visitors per CCPA/CPRA notice-at-collection requirements. */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/do-not-sell" element={<PrivacyChoices />} />
      
      {/* Auth routes */}
      <Route path="/auth" element={hasUserAndProfile ? <Navigate to={isSuperAdmin ? "/super-admin" : "/dashboard"} /> : <Auth onAuthSuccess={() => {}} />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/demo" element={<Demo />} />
      {/* Public signup is disabled — DashAlign is invite-only.
          Existing users redirect to their dashboard; visitors are sent to
          the login page (where they can request access if needed).
          The Signup/DemoSignup page components are intentionally left
          in the tree so that an invitation flow can re-enable them
          behind a token check later. */}
      <Route path="/demo-signup" element={<Navigate to={hasUserAndProfile ? (isSuperAdmin ? "/super-admin" : "/dashboard") : "/auth"} replace />} />
      <Route path="/onboard" element={<Onboard />} />
      {/* Juice It Up! Owners Conference lead form — the URL printed on the
          booth tablecloth and behind the flyer QR codes. Public, no auth;
          renders the same for logged-in users so the booth iPad works even
          while signed in. */}
      <Route path="/interest" element={<Interest />} />
      {/* New-hire paperwork wizard — public, tokenized (Phase 2 onboarding).
          New hires have no account; the invite token IS the credential. */}
      <Route path="/new-hire/:token" element={<NewHireOnboarding />} />
      <Route path="/signup" element={<Navigate to={hasUserAndProfile ? (isSuperAdmin ? "/super-admin" : "/dashboard") : "/auth"} replace />} />
      {/* /shared-login and /kiosk are the other kiosk-surface entry points
          alongside /limited (see KioskEntryGuard) — /shared-login unlocks a
          device then hands off into /limited, but /kiosk has its own
          self-serve PIN pad and quick-clock-in card that grant full
          kiosk-style access directly, without ever reaching /limited first.
          That was the gap #1129 reopened over: #1130 guarded /limited alone,
          so an already-authenticated scheduling-only EMAIL session could
          still reach the identical PIN pad on /kiosk from a personal phone. */}
      <Route
        path="/shared-login"
        element={
          <KioskEntryGuard
            hasUserAndProfile={!!hasUserAndProfile}
            isPinSession={isPinSession}
            role={profile?.role as Role}
            loginMethod={loginMethod}
          >
            <SharedLogin />
          </KioskEntryGuard>
        }
      />
      <Route
        path="/c/:companyCode/shared-login"
        element={
          <KioskEntryGuard
            hasUserAndProfile={!!hasUserAndProfile}
            isPinSession={isPinSession}
            role={profile?.role as Role}
            loginMethod={loginMethod}
          >
            <SharedLogin />
          </KioskEntryGuard>
        }
      />
      <Route
        path="/kiosk"
        element={
          <KioskEntryGuard
            hasUserAndProfile={!!hasUserAndProfile}
            isPinSession={isPinSession}
            role={profile?.role as Role}
            loginMethod={loginMethod}
          >
            <KioskView />
          </KioskEntryGuard>
        }
      />
      <Route
        path="/c/:companyCode/kiosk"
        element={
          <KioskEntryGuard
            hasUserAndProfile={!!hasUserAndProfile}
            isPinSession={isPinSession}
            role={profile?.role as Role}
            loginMethod={loginMethod}
          >
            <KioskView />
          </KioskEntryGuard>
        }
      />
      <Route
        path="/c/:companyCode/l/:locationCode/kiosk"
        element={
          <KioskEntryGuard
            hasUserAndProfile={!!hasUserAndProfile}
            isPinSession={isPinSession}
            role={profile?.role as Role}
            loginMethod={loginMethod}
          >
            <KioskView />
          </KioskEntryGuard>
        }
      />

      {/* Super Admin route */}
      <Route path="/super-admin" element={hasUserAndProfile ? <SuperAdmin /> : <Navigate to="/auth" />} />

      {/* Limited view routes — /limited has no route guard of its own (it
          must work for a shared kiosk device with no email session at all),
          but it does show a self-serve PIN pad that grants full kiosk-style
          access. For a role/platform mobile-mode already confines to
          scheduling-only, an active EMAIL session reaching that PIN pad is a
          bypass of that restriction (#1129), so those sessions are bounced
          to /dashboard before they ever see it. A real kiosk PIN session is
          never affected — see KioskEntryGuard. */}
      <Route
        path="/limited"
        element={
          <KioskEntryGuard
            hasUserAndProfile={!!hasUserAndProfile}
            isPinSession={isPinSession}
            role={profile?.role as Role}
            loginMethod={loginMethod}
          >
            <MainLayout userName={userName} userRole={userRole} />
          </KioskEntryGuard>
        }
      >
        <Route index element={<EmployeeLimitedView activeTab="dashboard" />} />
        <Route path="dashboard" element={<EmployeeLimitedView activeTab="dashboard" />} />
        <Route path="on-the-clock" element={<EmployeeLimitedView activeTab="on-the-clock" />} />
        <Route path="communications" element={<EmployeeLimitedView activeTab="communications" />} />
        <Route path="messages" element={<EmployeeLimitedView activeTab="messages" />} />
        <Route path="messages/:conversationId" element={<EmployeeLimitedView activeTab="messages" />} />
        <Route path="maintenance" element={<EmployeeLimitedView activeTab="maintenance" />} />
        <Route path="training" element={<EmployeeLimitedView activeTab="training" />} />
        <Route path="tasks" element={<EmployeeLimitedView activeTab="tasks" />} />
        <Route path="checklists" element={<EmployeeLimitedView activeTab="checklists" />} />
        {/* Checklist templates on the kiosk. Fresh Fill store leads sign in by
            PIN, so "Manage checklists" was a permission they could hold and
            never use — the only template surface lived inside the web-only
            MainLayout block below. The page self-gates on checklists.manage
            and every write goes through limited-daily-checklists, which
            re-checks the same capability server-side. */}
        <Route path="checklists/templates" element={<EmployeeLimitedView activeTab="checklist-templates" />} />
        {/* Task templates on the kiosk (portal ticket 52d08835). Alondra, a
            Juice It Up manager who signs in by PIN, holds canManageTasks but
            had no way to reach /tasks/templates (web-only MainLayout). The
            page self-gates on canCreateTaskTemplate and every write goes
            through limited-tasks save_template / update_template /
            delete_template, which re-check the same role server-side. */}
        <Route path="tasks/templates" element={<EmployeeLimitedView activeTab="task-templates" />} />
        <Route path="temperature-logs" element={<EmployeeLimitedView activeTab="temperature-logs" />} />
        <Route path="restroom-checks" element={<EmployeeLimitedView activeTab="restroom-checks" />} />
        <Route path="leaderboard" element={<EmployeeLimitedView activeTab="leaderboard" />} />
        <Route path="badges" element={<EmployeeLimitedView activeTab="badges" />} />
        <Route path="shift-checkins" element={<EmployeeLimitedView activeTab="shift-checkins" />} />
        <Route path="callout-monitor" element={<EmployeeLimitedView activeTab="callout-monitor" />} />
        {/* Kiosk timesheet pre-approval for a Store Lead (#2127) — the web
            Timesheets surface (#940) has no kiosk twin, so a PIN-only
            lead_manager could not pre-approve their location's punches
            without a web login. The limited-timesheets edge fn re-checks
            the role + same-location gate server-side. */}
        <Route path="timesheets" element={<EmployeeLimitedView activeTab="timesheets" />} />
        <Route path="receive-invoice" element={<EmployeeLimitedView activeTab="receive-invoice" />} />
        {/* Fillable agreements — a PIN team member signs their own assignments,
            a lead countersigns; the limited-agreements edge fn enforces
            per-assignment ownership + the leadership gate server-side. */}
        <Route path="agreements" element={<EmployeeLimitedView activeTab="agreements" />} />
        {/* Store sales on the kiosk. The card itself shipped with #1302 but was
            only ever rendered inside the Log Waste screen, so a Store Lead had
            no way to reach today's numbers without going to log waste. */}
        <Route path="sales" element={<EmployeeLimitedView activeTab="sales" />} />
        {/* PAR Sheets on the kiosk (#1907) — a Fresh Fill Store Lead signs in
            by PIN and had no route to the web-only /par-sheets page even
            though the page itself is open to any store role. */}
        <Route path="par-sheets" element={<EmployeeLimitedView activeTab="par-sheets" />} />
        <Route path="log-waste" element={<EmployeeLimitedView activeTab="log-waste" />} />
        <Route path="log-consumption" element={<EmployeeLimitedView activeTab="log-consumption" />} />
        {/* Where PIN staff hear back about a bug or request they filed — they
            have no mailbox, so the portal is the only feedback channel. */}
        <Route path="my-tickets" element={<EmployeeLimitedView activeTab="my-tickets" />} />
        <Route path="surveys" element={<EmployeeLimitedView activeTab="surveys" />} />
        {/* DashAlign kiosk schedule is gated per-location by
            locations.kiosk_schedule_enabled (default off; on for the Visalia
            pilot). EmployeeLimitedView self-redirects to the dashboard when
            the switch is off, and the limited-schedule edge fn enforces the
            same gate server-side. */}
        <Route path="schedule" element={<EmployeeLimitedView activeTab="schedule" />} />
        {/* DashAlign kiosk clock is now gated per-location by
            locations.kiosk_clock_enabled (default off; on for Visalia).
            EmployeeLimitedView self-redirects to the dashboard when the
            switch is off, so stores still on the Homebase flow can't reach
            this surface by direct URL. */}
        <Route path="time-clock" element={<EmployeeLimitedView activeTab="time-clock" />} />
      </Route>

      {/* Main app routes - available to all authenticated users except PIN sessions and super admins */}
      {hasUserAndProfile && !isPinSession && !isSuperAdmin && (
        <Route path="/" element={<MobileRouteGuard><MainLayout userName={userName} userRole={userRole} /></MobileRouteGuard>}>
          <Route path="dashboard" element={<Dashboard />} />
          {/* Manager Mobile Dashboard retired 2026-06-09 — managers
              now use /dashboard like everyone else (the page's
              cost / open-shift / broadcast surfaces were removed
              for managers, leaving it effectively empty). The
              /manager-mobile URL redirects to /dashboard so any
              bookmarks / push notifications keep working. */}
          <Route path="manager-mobile" element={<Navigate to="/dashboard" replace />} />
          <Route path="communications" element={<TeamCommsGated />} />
          <Route path="messages" element={<Messages />} />
          <Route path="messages/:conversationId" element={<Messages />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="tasks/templates" element={<TaskTemplates />} />
          <Route path="training" element={<Training />} />
          <Route path="maintenance" element={<Maintenance />} />
          <Route path="schedules" element={<Schedules />} />
          {/* Mobile-only scheduling messages (surface='mobile', isolated from
              kiosk/web). Under /schedules so it inherits the scheduling-only
              allow-list; the inbox itself is a phone surface. */}
          <Route path="schedules/messages" element={<SchedulesMessages />} />
          {/* Unsent thread. Static segment so it outranks :conversationId —
              the anchor rides in the query string until the first send
              creates the conversation (src/lib/messaging/mobile-draft-thread.ts). */}
          <Route path="schedules/messages/new" element={<SchedulesMessages />} />
          <Route path="schedules/messages/:conversationId" element={<SchedulesMessages />} />
          <Route path="schedules/timesheets" element={<SchedulesTimesheets />} />
          {/* Payroll: the Timesheets page in payroll mode (exports up front).
              The page re-checks payroll.export itself, since hiding the
              sidebar link leaves the URL typeable. */}
          <Route path="schedules/payroll" element={<SchedulesTimesheets view="payroll" />} />
          <Route path="schedules/time-off" element={<SchedulesTimeOff />} />
          <Route path="schedules/work-trips" element={<SchedulesWorkTrips />} />
          <Route path="schedules/requests" element={<SchedulesRequests />} />
          <Route path="schedules/availability" element={<SchedulesAvailability />} />
          <Route path="schedules/compliance" element={<SchedulesCompliance />} />
          {/* Break Log (#feedback fa1b7c23): the staggered break sheet a Store
              Lead posts each morning. Rolled out to Fresh Fill Visalia only —
              the page re-checks that itself (lib/break-schedule-access.ts),
              since hiding the sidebar link leaves the URL typeable. */}
          <Route path="schedules/break-log" element={<SchedulesBreakLog />} />
          {/* Tips: Juice It Up only (tip_pooling). The page checks role and the
              feature itself, since hiding the sidebar link leaves the URL typeable. */}
          <Route path="schedules/tips" element={<SchedulesTips />} />
          <Route path="schedules/insights" element={<SchedulesInsights />} />
          <Route path="schedules/clock" element={<Timeclock />} />
          <Route path="timeclock" element={<Timeclock />} />
          {/* On the Clock — live roster of who's working now, all roles. */}
          <Route path="on-the-clock" element={<OnTheClock />} />
          {/* Daily Logs hub (2026-07 nav consolidation): Temp Log, Restroom
              Checks, and Shift Check-ins are tabs on one page. The old
              standalone paths redirect with the matching tab param so
              bookmarks, notification links, and help tours keep working.
              Kiosk (/limited/*) routes above are intentionally untouched. */}
          <Route path="daily-logs" element={<FeatureRedirect feature="daily_logs"><DailyLogs /></FeatureRedirect>} />
          <Route path="restroom-checks" element={<Navigate to="/daily-logs?tab=restroom-checks" replace />} />
          <Route path="analytics" element={<Analytics />} />
          {/* Reports hub. The catalog and each individual report share one
              page because they share the access gate and the favourites
              state; the page redirects an unknown or forbidden slug back to
              /reports rather than 404ing. Per-report role gating lives in
              lib/reports-registry — Punch Audit stays admin/corporate, the
              gating it had when it lived inside Settings. */}
          <Route path="reports" element={<Reports />} />
          <Route path="reports/:slug" element={<Reports />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/notifications" element={<NotificationSettings />} />
          <Route path="settings/notifications/admin" element={<AdminNotifications />} />
          <Route path="account" element={<Account />} />
          <Route path="help" element={<Help />} />
          <Route path="checklists" element={<DailyChecklists />} />
          <Route path="checklists/templates" element={<DailyChecklistTemplates />} />
          {/* Leadership report over prep quantities captured on checklists.
              The page redirects non-leadership roles to /checklists itself. */}
          <Route path="checklists/prep-history" element={<PrepHistory />} />
          {/* Corporate-facing roll-up: completion rate / on-time / missed
              across the stores the viewer can see. Store staff hitting this
              URL directly are redirected to /checklists by the page itself.
              See docs/CHECKLIST_COMPLETION_METRICS.md for the math. */}
          <Route path="checklists/completion-report" element={<ChecklistCompletionReport />} />
          {/* Digital PAR sheets (#1379) — par level + on-hand count → vendor
              order quantity, for the ordering guides that used to be paper. */}
          <Route path="par-sheets" element={<FeatureRedirect feature="par_sheets"><ParSheets /></FeatureRedirect>} />
          <Route path="temperature-logs" element={<Navigate to="/daily-logs?tab=temperature-logs" replace />} />
          <Route path="shift-checkins" element={<Navigate to="/daily-logs?tab=shift-checkins" replace />} />
          <Route path="cogs" element={<CogsIntelligence />} />
          {/* Waste log lives inside the COGS hub as the "Waste" tab, which
              got lost among ten other tabs (ticket #1295 reopened: a
              corporate user couldn't find it and asked whether it would be
              under Insights). Give it its own nav entry + deep link so it
              shows up by name instead of requiring tab-hunting. */}
          <Route path="waste-log" element={<Navigate to="/cogs" replace state={{ cogsTab: 'waste' }} />} />
          {/* Brand-standards audits + opening playbooks (PR 7). The page
              redirects non-admin/corporate roles to /dashboard itself. */}
          <Route path="brand-standards" element={<FeatureRedirect feature="brand_standards"><BrandStandards /></FeatureRedirect>} />
          {/* Team Surveys — admin/corporate authoring + review. The page
              redirects non-admin/corporate roles to /dashboard itself.
              Both survey routes sit behind the `surveys` company feature, so a
              tenant with surveys held off gets "not enabled" rather than an
              authoring screen for a workflow they were never onboarded to —
              the sidebar rows are already gone (surveys/visibility.ts). */}
          <Route path="team-surveys" element={<FeatureGate feature="surveys"><TeamSurveys /></FeatureGate>} />
          {/* Web self-service: an account-holder completing their own assigned
              survey. Open to every authenticated user; the edge function scopes
              strictly to the caller's own responses. */}
          <Route path="my-surveys" element={<FeatureGate feature="surveys"><MySurveys /></FeatureGate>} />
          {/* Open to every authenticated user — RLS scopes the list to the
              tickets they personally submitted, so there is no role gate. */}
          {/* Route path stays /my-tickets — resolved-ticket notifications
              deep-link here (?ticket=) and mobile-mode keys off the prefix. The
              page itself is now "Tickets" (adds an All Tickets tab for leadership). */}
          <Route path="my-tickets" element={<Tickets />} />
          {/* A JWT user's own agreements to sign / countersign. Open to all;
              the agreements edge function scopes to the caller's assignments.
              Agreement notifications deep-link here as ?open=<assignmentId>. */}
          <Route path="my-agreements" element={<FeatureRedirect feature="my_agreements"><MyAgreements /></FeatureRedirect>} />
          {/* In-app meal period waiver assignment (ticket: in-app meal waiver,
              2026-10-24). The notification bell routes here; the page asks the
              server to mint a fresh invite token for the caller's OWN packet
              and forwards to /new-hire/:token so one signing UI covers both
              the email and in-app paths. Open to all JWT roles -- the
              onboarding-packet edge function scopes it to the caller. */}
          <Route path="my-waiver/:packetId" element={<MyWaiver />} />
          {/* HR document vault. Access = admin/super_admin OR company_hr_grants
              row; the page redirects everyone else to /dashboard. */}
          <Route path="employee-files" element={<FeatureRedirect feature="employee_files"><EmployeeFiles /></FeatureRedirect>} />
          {/* Associate phone list (#1896). The page repeats the users.manage
              and scheduling-only checks because the URL is typeable. Not
              mounted under /limited: a kiosk PIN session must not read
              coworker numbers. */}
          <Route path="phone-list" element={<AssociatePhoneList />} />
          {/* Associate counseling notices, filed digitally (#1375). Open to
              all JWT roles — RLS scopes leadership to every notice at the
              company and an associate to only their own. Fresh Fill handbook
              process, so gated on `counseling_sessions` (off by default). */}
          <Route path="counseling-sessions" element={<FeatureGate feature="counseling_sessions"><CounselingSessions /></FeatureGate>} />
          {/* The vendor-invoice inbox now lives in the COGS hub's Approvals
              tab; redirect the old standalone path there so bookmarks/links
              still work and there's a single place to approve invoices. */}
          <Route path="vendor-invoices" element={<Navigate to="/cogs" replace state={{ cogsTab: 'approvals' }} />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          {/* Badges merged into the Recognition page as a tab (2026-07);
              the kiosk /limited/badges surface is unchanged. */}
          <Route path="badges" element={<Navigate to="/leaderboard?tab=badges" replace />} />
          <Route index element={<Navigate to="/dashboard" />} />
        </Route>
      )}

      {/* Root path:
            - Authenticated users redirect to their dashboard / limited / super-admin
            - Logged-out visitors on the WEB see the marketing landing page
            - Logged-out visitors in the NATIVE iOS/Android app skip Landing
              and go straight to /auth — the mobile app is invite-only B2B
              software for existing customers, not a marketing surface.
          The earlier {hasUserAndProfile && ...} block at line ~228 also
          handles "/" for the authenticated MainLayout; this fallback only
          fires when that conditional render is absent. */}
      <Route
        path="/"
        element={(() => {
          const target = resolveRootRoute({
            isNative: Capacitor.isNativePlatform(),
            loading,
            hasUserAndProfile: !!hasUserAndProfile,
            isSuperAdmin,
            isPinSession: !!isPinSession,
            isEmployeeWithValidPinSession: isEmployee && hasValidPinSession,
            hasKioskDeviceSession: readKioskDeviceSessionToken(localStorage) !== null,
          });
          switch (target) {
            case 'limited':
              return <Navigate to="/limited" />;
            case 'super-admin':
              return <Navigate to="/super-admin" />;
            case 'dashboard':
              return <Navigate to="/dashboard" />;
            case 'auth':
              return <Navigate to="/auth" replace />;
            case 'landing':
              return <Landing />;
            default:
              // Native, still resolving auth — hold here instead of
              // flashing /auth for an already-authenticated user (#817).
              return <BrandedLoader />;
          }
        })()}
      />
      <Route
        path="*"
        element={
          isPinSession || (isEmployee && hasValidPinSession)
            ? <Navigate to="/limited" />
            // Same rule as "/" above: an unknown route on a kiosk tablet belongs
            // on the PIN pad, not at an email login it can never satisfy.
            : readKioskDeviceSessionToken(localStorage) !== null
              ? <Navigate to="/limited" />
            : hasUserAndProfile
              ? (isSuperAdmin ? <Navigate to="/super-admin" /> : <NotFound />)
              : <Navigate to="/auth" />
        }
      />
      </Routes>
      </Suspense>
      </BiometricLockGuard>
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem
        disableTransitionOnChange={false}
      >
        <AuthProvider>
          {/* Restores last session's dashboard data from disk before anything
              below reads the query cache, so a returning launch paints numbers
              instead of skeletons (#1246). Email logins only; see
              src/lib/query-cache-persist.ts. */}
          <QueryCachePersistence>
          {/* Company permission rows, read once here and shared by every
              role gate via usePolicyMap(). Must sit inside AuthProvider
              (it keys on profile.company_id) and inside
              QueryClientProvider. */}
          <PermissionsProvider>
          <CompanyProvider>
            <ErrorBoundary>
              <NotificationProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  {/* Native-only "update in the App Store" nudge (no-ops on web). */}
                  <UpdateAvailableBanner />
                  <BrowserRouter>
                    <ProviderWrapper>
                      <AppContent />
                    </ProviderWrapper>
                  </BrowserRouter>
                </TooltipProvider>
              </NotificationProvider>
            </ErrorBoundary>
          </CompanyProvider>
          </PermissionsProvider>
          </QueryCachePersistence>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
